import { addDays, addMonths, format, isValid, parseISO, setDate, startOfMonth } from 'date-fns';

export const EXTRACTED_DUE_DATE = 'extracted';
export const THIRTY_DAYS_EOM = '30-eom';
export const SIXTY_DAYS_EOM = '60-eom';

const toDateInput = (value) => (value ? String(value).slice(0, 10) : '');
const amount = (money) => (Number.isFinite(money?.amount) ? money.amount : '');
export const optionalNumber = (value) => (value === '' || value === null || value === undefined ? null : Number(value));

const finiteNumber = (value) => {
  if (value === '' || value === null || value === undefined || !Number.isFinite(Number(value))) return null;
  return Number(value);
};

const roundedMoney = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
const moneyMatches = (left, right) =>
  left !== null && right !== null && Math.round(left * 100) === Math.round(right * 100);
const validationMoney = (value) => `$${Number(value).toFixed(2)}`;

export const calculateLineTotal = (quantity, unitPrice) => {
  const numericQuantity = finiteNumber(quantity);
  const numericUnitPrice = finiteNumber(unitPrice);
  if (numericQuantity === null || numericUnitPrice === null) return null;
  return roundedMoney(numericQuantity * numericUnitPrice);
};

export const isLineAmountValid = (quantity, unitPrice, lineAmount) => {
  return moneyMatches(calculateLineTotal(quantity, unitPrice), finiteNumber(lineAmount));
};

const invoiceMoney = (invoice, field) => finiteNumber(invoice?.[`${field}Amount`] ?? invoice?.[field]?.amount);

export const validateInvoiceTotals = (invoice) => {
  const lineItems = invoice?.lineItems || [];
  const calculatedLineTotals = lineItems.map((lineItem) =>
    calculateLineTotal(lineItem.quantity, lineItem.unitPrice?.amount)
  );
  const extractedLineAmounts = lineItems.map((lineItem) => finiteNumber(lineItem.amount?.amount));
  const missingCalculatedLines = calculatedLineTotals.filter((lineTotal) => lineTotal === null).length;
  const missingExtractedLines = extractedLineAmounts.filter((lineAmount) => lineAmount === null).length;
  const calculatedSubtotal = missingCalculatedLines
    ? null
    : roundedMoney(calculatedLineTotals.reduce((totalValue, lineTotal) => totalValue + lineTotal, 0));
  const extractedLineTotal = missingExtractedLines
    ? null
    : roundedMoney(extractedLineAmounts.reduce((total, lineAmount) => total + lineAmount, 0));
  const subtotal = invoiceMoney(invoice, 'subtotal');
  const totalTax = invoiceMoney(invoice, 'totalTax');
  const total = invoiceMoney(invoice, 'total');
  const issues = [];

  if (!lineItems.length) {
    issues.push('No line items are available to validate the invoice total.');
  } else if (missingCalculatedLines) {
    issues.push(
      `${missingCalculatedLines} line ${
        missingCalculatedLines === 1 ? 'is' : 'items are'
      } missing a quantity or item price.`
    );
  }

  if (subtotal === null) {
    issues.push('The invoice subtotal is missing.');
  } else {
    if (calculatedSubtotal !== null && !moneyMatches(calculatedSubtotal, subtotal)) {
      issues.push(
        `Calculated line totals are ${validationMoney(
          calculatedSubtotal
        )}, but the invoice subtotal is ${validationMoney(subtotal)}` +
          '. Check whether item prices include GST (divide by 1.1) or are pack prices that need conversion to a per-unit price.'
      );
    }
    if (extractedLineTotal !== null && !moneyMatches(extractedLineTotal, subtotal)) {
      issues.push(
        `Extracted GST-exclusive line amounts total ${validationMoney(
          extractedLineTotal
        )}, but the invoice subtotal is ${validationMoney(subtotal)}.`
      );
    }
  }

  if (total === null) {
    issues.push('The invoice total is missing.');
  } else if (subtotal !== null) {
    if (extractedLineTotal !== null) {
      const extractedTax =
        totalTax ?? (moneyMatches(extractedLineTotal, total) ? 0 : roundedMoney(extractedLineTotal * 0.1));
      const extractedTotalWithTax = roundedMoney(extractedLineTotal + extractedTax);
      if (!moneyMatches(extractedTotalWithTax, total)) {
        issues.push(
          `Extracted GST-exclusive line amounts plus GST are ${validationMoney(
            extractedTotalWithTax
          )}, but the invoice total is ${validationMoney(total)}.`
        );
      }
    }
    const effectiveTax = totalTax ?? (moneyMatches(subtotal, total) ? 0 : roundedMoney(subtotal * 0.1));
    const calculatedInvoiceTotal = roundedMoney(subtotal + effectiveTax);
    if (!moneyMatches(calculatedInvoiceTotal, total)) {
      issues.push(
        `Subtotal plus GST is ${validationMoney(calculatedInvoiceTotal)}, but the invoice total is ${validationMoney(
          total
        )}.`
      );
    }
  }

  return {
    valid: issues.length === 0,
    issues,
    message: issues.join(' '),
    calculatedSubtotal,
    extractedLineTotal
  };
};

export const calculateEomDueDate = (invoiceDate, monthsAfterInvoiceMonth) => {
  if (!invoiceDate) return '';
  const parsedInvoiceDate = parseISO(String(invoiceDate).slice(0, 10));
  if (!isValid(parsedInvoiceDate)) return '';
  const targetMonth = startOfMonth(addMonths(parsedInvoiceDate, monthsAfterInvoiceMonth));
  return format(setDate(targetMonth, 30), 'yyyy-MM-dd');
};

export const dueDateFromVendorTerm = (invoiceDate, currentDueDate, term) => {
  if (!invoiceDate) return currentDueDate || '';
  const parsedInvoiceDate = parseISO(String(invoiceDate).slice(0, 10));
  if (!isValid(parsedInvoiceDate)) return currentDueDate || '';

  const termType = String(term?.Type || '').toUpperCase();
  if (termType === 'STANDARD' && Number.isFinite(Number(term?.DueDays))) {
    return format(addDays(parsedInvoiceDate, Number(term.DueDays)), 'yyyy-MM-dd');
  }

  const normalizedTerm = String(term?.name || term?.Name || term?.value || term || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const dateDrivenMonths =
    termType === 'DATE_DRIVEN' && Number(term?.DueNextMonthDays) > 0
      ? Math.ceil(Number(term.DueNextMonthDays) / 30)
      : null;
  const monthsAfterInvoiceMonth =
    dateDrivenMonths ||
    (normalizedTerm === '30 eom' || normalizedTerm === '30 days eom' || /net 30.*after eom/.test(normalizedTerm)
      ? 1
      : normalizedTerm === '60 eom' || normalizedTerm === '60 days eom' || /net 60.*after eom/.test(normalizedTerm)
      ? 2
      : null);
  if (monthsAfterInvoiceMonth === null) return currentDueDate || '';
  const targetMonth = startOfMonth(addMonths(parsedInvoiceDate, monthsAfterInvoiceMonth));
  const dayOfMonth = Number(term?.DayOfMonthDue) || 30;
  return format(setDate(targetMonth, dayOfMonth), 'yyyy-MM-dd');
};

export const emptyLineItem = (currency = 'AUD', defaultTaxCode = null) => ({
  sku: '',
  description: '',
  boxshallName: '',
  boxshallDescription: '',
  boxshallProduct: null,
  quantity: '',
  unit: '',
  unitPrice: { amount: '', currency },
  tax: { amount: '', currency },
  amount: { amount: '', currency },
  billableStatus: false,
  taxCode: defaultTaxCode,
  markupPercent: '',
  customerRef: null,
  classRef: null
});

const normalizeProductSearchValue = (value) =>
  String(value || '')
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export const boxshallProductOption = (product) => {
  const name = product.Name || product.name || product.FullyQualifiedName || product.label;
  const description = product.Description || product.description || '';
  const searchAliases = product.searchAliases || {};
  const localId = product._id || product.id || product.localId || null;
  const qboId = product.Id || product.qboId || null;
  return {
    value: String(localId || qboId || ''),
    localId: localId ? String(localId) : null,
    qboId: qboId ? String(qboId) : '',
    name,
    label: name,
    description,
    searchValues: [
      name,
      description,
      ...(searchAliases.boxshallDescriptions || []),
      ...(searchAliases.supplierNames || []),
      ...(searchAliases.productServiceDescriptions || []),
      ...(searchAliases.productServices || [])
    ].filter(Boolean)
  };
};

export const filterBoxshallProductOption = ({ data }, inputValue) => {
  return scoreBoxshallProductOption(data, inputValue).matchedWords > 0;
};

export const scoreBoxshallProductOption = (option, inputValue) => {
  const search = normalizeProductSearchValue(inputValue);
  if (!search) return { matchedWords: 0, score: 0 };
  const searchWords = search.split(' ').filter((word, index, words) => word && words.indexOf(word) === index);
  const normalizedValues = (option.searchValues || [option.label, option.value])
    .map(normalizeProductSearchValue)
    .filter(Boolean);
  const candidateWords = normalizedValues.flatMap((value) => value.split(' '));
  let matchedWords = 0;
  let score = normalizedValues.some((value) => value.includes(search)) ? searchWords.length * 5 : 0;

  searchWords.forEach((searchWord) => {
    const wordScore = candidateWords.reduce((bestScore, candidateWord) => {
      if (candidateWord === searchWord) return Math.max(bestScore, 4);
      if (candidateWord.startsWith(searchWord)) return Math.max(bestScore, 3);
      if (candidateWord.includes(searchWord)) return Math.max(bestScore, 2);
      return bestScore;
    }, 0);
    if (wordScore) matchedWords += 1;
    score += wordScore;
  });

  return { matchedWords, score };
};

export const rankBoxshallProductOptions = (options, inputValue) => {
  const search = normalizeProductSearchValue(inputValue);
  if (!search) return options;
  return options
    .map((option, index) => ({ option, index, ...scoreBoxshallProductOption(option, search) }))
    .filter(({ matchedWords }) => matchedWords > 0)
    .sort(
      (left, right) => right.matchedWords - left.matchedWords || right.score - left.score || left.index - right.index
    )
    .map(({ option }) => option);
};

export const toGridRows = (invoice, defaultTaxCode = null) => {
  const lineItems = invoice.lineItems?.length ? invoice.lineItems : [{}];
  const normalizedLineItems = lineItems.map((lineItem) => ({
    ...lineItem,
    taxCode:
      lineItem.taxCode === null
        ? null
        : lineItem.taxCode && typeof lineItem.taxCode === 'object'
        ? lineItem.taxCode
        : defaultTaxCode
  }));
  const invoiceTotalValidation = validateInvoiceTotals({ ...invoice, lineItems: normalizedLineItems });
  return normalizedLineItems
    .map((lineItem, lineIndex) => ({ lineItem, lineIndex }))
    .filter(({ lineItem }) => lineItem.purchaseTransfer?.status !== 'Created')
    .map(({ lineItem, lineIndex }) => ({
      ...invoice,
      lineItems: normalizedLineItems,
      id: `${invoice._id}:${lineIndex}`,
      invoiceId: invoice._id,
      lineIndex,
      receivedAt: invoice.source?.receivedAt || invoice.createdAt,
      supplierName: invoice.supplier?.name || '',
      supplierValue: invoice.supplier?.value || null,
      supplierLinked: invoice.supplierLinked ?? Boolean(invoice.supplier?.value),
      supplierAddress: invoice.supplier?.address || '',
      supplierTaxId: invoice.supplier?.taxId || '',
      invoiceDate: toDateInput(invoice.invoiceDate),
      dueDate: toDateInput(invoice.dueDate),
      subtotalAmount: amount(invoice.subtotal),
      totalTaxAmount: amount(invoice.totalTax),
      totalAmount: amount(invoice.total),
      amountDueAmount: amount(invoice.amountDue),
      productService: lineItem.sku || '',
      boxshallName: lineItem.boxshallName || '',
      boxshallDescription: lineItem.boxshallDescription || '',
      boxshallProductId: lineItem.boxshallProduct?.id || null,
      boxshallProductValue: lineItem.boxshallProduct?.value || null,
      boxshallProductLinked: lineItem.boxshallProductLinked ?? Boolean(lineItem.boxshallProduct?.id),
      productServiceDescription: lineItem.description || '',
      quantity: lineItem.quantity ?? '',
      productServiceRate: amount(lineItem.unitPrice),
      taxAmount: amount(lineItem.tax),
      amount: amount(lineItem.amount),
      calculatedTotal: calculateLineTotal(lineItem.quantity, lineItem.unitPrice?.amount),
      invoiceTotalsValid: invoiceTotalValidation.valid,
      invoiceTotalsMessage: invoiceTotalValidation.message,
      billableStatus: lineItem.billableStatus ?? false,
      taxCode: lineItem.taxCode,
      markupPercent: lineItem.markupPercent ?? '',
      customerRef: lineItem.customerRef || null,
      classRef: lineItem.classRef || null,
      purchaseTransfer: lineItem.purchaseTransfer || null,
      actions: ''
    }));
};

const PROPAGATED_REFERENCE_FIELDS = ['taxCode'];

export const rowToPayload = (row, replaceGridLine = false, referenceFieldsToPropagate = []) => {
  let lineItems = row.lineItems || [];
  if (replaceGridLine) {
    if (!lineItems.length) {
      lineItems = [lineItemFromGridRow(row)];
    } else {
      lineItems = lineItems.map((lineItem, index) =>
        index === row.lineIndex ? lineItemFromGridRow(row, lineItem) : lineItem
      );
    }

    const referenceFields = PROPAGATED_REFERENCE_FIELDS.filter((field) => referenceFieldsToPropagate.includes(field));
    if (referenceFields.length) {
      lineItems = lineItems.map((lineItem) =>
        referenceFields.reduce(
          (updatedLineItem, field) => ({ ...updatedLineItem, [field]: row[field] || null }),
          lineItem
        )
      );
    }
  }

  const supplierSelection = row.supplierName && typeof row.supplierName === 'object' ? row.supplierName : null;

  return {
    supplier: {
      name: supplierSelection?.extractedName || supplierSelection?.qboName || row.supplierName,
      value: supplierSelection ? supplierSelection.value : row.supplierValue,
      ...((supplierSelection?.qboName || row.supplierQboName) && {
        qboName: supplierSelection?.qboName || row.supplierQboName
      }),
      address: row.supplierAddress,
      taxId: row.supplierTaxId
    },
    invoiceNumber: row.invoiceNumber,
    invoiceDate: row.invoiceDate || null,
    dueDate: row.dueDate || null,
    ...(row.dueDateSource && { dueDateSource: row.dueDateSource }),
    project: row.project,
    purchaseOrder: row.purchaseOrder,
    currency: row.currency,
    subtotal: { amount: optionalNumber(row.subtotalAmount), currency: row.currency },
    totalTax: { amount: optionalNumber(row.totalTaxAmount), currency: row.currency },
    total: { amount: optionalNumber(row.totalAmount), currency: row.currency },
    amountDue: { amount: optionalNumber(row.amountDueAmount), currency: row.currency },
    lineItems
  };
};

export const rowToPayloadAfterLineItemDeletion = (row, deletedLineIndexes = []) =>
  rowToPayload(
    {
      ...row,
      lineItems: (row.lineItems || []).filter((_lineItem, index) => !deletedLineIndexes.includes(index))
    },
    false
  );

export const deletesWholeInvoice = (row, deletedLineIndexes = []) => {
  const remainingLineItems = (row.lineItems || []).filter((_lineItem, index) => !deletedLineIndexes.includes(index));
  return remainingLineItems.length === 0;
};

export const lineItemFromGridRow = (row, original = {}) => ({
  ...original,
  sku: row.productService,
  description: row.productServiceDescription,
  boxshallName: row.boxshallName && typeof row.boxshallName === 'object' ? row.boxshallName.name : row.boxshallName,
  boxshallDescription:
    row.boxshallName && typeof row.boxshallName === 'object'
      ? row.boxshallDescription || row.boxshallName.description || ''
      : row.boxshallDescription,
  boxshallProduct:
    row.boxshallName && typeof row.boxshallName === 'object'
      ? row.boxshallName.qboId
        ? {
            ...(row.boxshallName.value && { id: row.boxshallName.value }),
            value: row.boxshallName.qboId,
            name: row.boxshallName.name
          }
        : null
      : row.boxshallProductId
      ? {
          id: row.boxshallProductId,
          value: row.boxshallProductValue,
          name: row.boxshallName
        }
      : null,
  quantity: optionalNumber(row.quantity),
  unitPrice: {
    ...(original.unitPrice || {}),
    amount: optionalNumber(row.productServiceRate),
    currency: row.currency || 'AUD'
  },
  amount: {
    ...(original.amount || {}),
    amount: optionalNumber(row.amount),
    currency: row.currency || 'AUD'
  },
  billableStatus: Boolean(row.billableStatus),
  taxCode: row.taxCode || null,
  markupPercent: optionalNumber(row.markupPercent),
  customerRef: row.customerRef || null,
  classRef: row.classRef || null
});
